import React, { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import { internalChatAPI, usersAPI } from "../services/api";
import VoiceRecordingControls from "../components/VoiceRecordingControls";
import {
  Send,
  Users,
  MessageCircle,
  Circle,
  Paperclip,
  Mic,
  Trash2,
  Check,
  CheckCheck,
  Clock,
  ArrowLeft,
  Reply,
  Pencil,
  X,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Download,
} from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Textarea } from "@/components/ui/textarea";

const renderStatus = (msg) => {
  if (msg.sender_id !== msg.user?.id) return null;

  switch (msg.status) {
    case "sent":
      return <Check className="w-3 h-3 text-gray-500" />;
    case "delivered":
      return <CheckCheck className="w-3 h-3 text-gray-500" />;
    case "read":
      return <CheckCheck className="w-3 h-3 text-cyan-500" />;
    default:
      return <Clock className="w-3 h-3 text-gray-500 opacity-50" />;
  }
};

const InternalChat = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});

  const [replyingTo, setReplyingTo] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [isPreparingAudio, setIsPreparingAudio] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);

  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [unreadInChat, setUnreadInChat] = useState(0);

  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const recordingElapsedRef = useRef(0);
  const recordingStartedAtRef = useRef(null);
  const recordingRequestRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const audioPreviewUrlRef = useRef(null);
  const finishRecordingRef = useRef(null);
  const audioSendInFlightRef = useRef(false);
  const recipientId = selectedUser?.id;
  const canSendAudio = Boolean(recipientId) && selectedUser.is_active !== false;
  const activeRecipientIdRef = useRef(recipientId);
  activeRecipientIdRef.current = recipientId;
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isUserScrolledUp = useRef(false);
  const messageInputRef = useRef(null);
  const editInputRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const prevLatestMessageIdRef = useRef(null);
  const currentChatId = useRef(selectedUser?.id);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);

  useEffect(() => {
    setRecordedAudio(null);
    setIsStartingRecording(false);
    setIsPreparingAudio(false);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);

    return () => {
      recordingRequestRef.current = null;
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.ondataavailable = null;
        if (recorder.state !== "inactive") recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
        mediaRecorderRef.current = null;
      }
      audioChunksRef.current = [];
      clearInterval(timerRef.current);
      recordingElapsedRef.current = 0;
      recordingStartedAtRef.current = null;
      finishRecordingRef.current?.(null);
      finishRecordingRef.current = null;
      if (audioPreviewUrlRef.current) {
        URL.revokeObjectURL(audioPreviewUrlRef.current);
        audioPreviewUrlRef.current = null;
      }
    };
  }, [recipientId, canSendAudio]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && zoomedImage) {
        setZoomedImage(null);
        setZoomScale(1);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [zoomedImage]);

  const handleDownload = async (e, url, filename) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      toast({ title: "Iniciando download...", description: filename });
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erro ao baixar:", error);
      toast({
        title: "Erro",
        description: "O download bloqueou. Abrindo arquivo...",
        variant: "destructive",
      });
      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    loadUsers();
    loadOnlineUsers();
    loadUnreadCounts();

    pollingRef.current = setInterval(() => {
      loadUsers();
      if (selectedUser) {
        loadMessages(selectedUser.id);
      }
      loadOnlineUsers();
      loadUnreadCounts();
      sendHeartbeat();
    }, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [selectedUser]);

  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.style.height = "auto";
      const scrollHeight = messageInputRef.current.scrollHeight;
      messageInputRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  useEffect(() => {
    if (selectedUser?.id) {
      const savedDraft = sessionStorage.getItem(`draft_internal_${selectedUser.id}`);
      if (savedDraft) {
        setNewMessage(savedDraft);
      } else {
        setNewMessage("");
      }
    } else {
      setNewMessage("");
      setMessages([]);
      setReplyingTo(null);
      setEditingMessageId(null);
      setIsDragging(false);
      setZoomedImage(null);
    }
  }, [selectedUser?.id]);

  const handleMessageChange = (e) => {
    const text = e.target.value;
    setNewMessage(text);
    
    if (selectedUser?.id) {
      sessionStorage.setItem(`draft_internal_${selectedUser.id}`, text);
    }
  };

  const scrollToBottom = (instant = false, force = false) => {
    if (isUserScrolledUp.current && !force) return;

    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: instant ? "auto" : "smooth",
      });
      setUnreadInChat(0);
      isUserScrolledUp.current = false;
      setIsScrolledUp(false);
    }
  };

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 150;

    isUserScrolledUp.current = !isAtBottom;
    setIsScrolledUp(!isAtBottom);
    if (isAtBottom && unreadInChat > 0) setUnreadInChat(0);
  };

  useEffect(() => {
    const latestMessage = messages[messages.length - 1];
    if (selectedUser?.id !== currentChatId.current) {
      currentChatId.current = selectedUser?.id;
      isUserScrolledUp.current = false;
      setIsScrolledUp(false);
      setUnreadInChat(0);
      prevMessagesLengthRef.current = messages.length;
      prevLatestMessageIdRef.current = latestMessage?.id;

      scrollToBottom(true);
      setTimeout(() => scrollToBottom(true), 100);
      return;
    }

    const latestMessageChanged = latestMessage?.id && prevLatestMessageIdRef.current &&
      latestMessage.id !== prevLatestMessageIdRef.current;
    if (messages.length > prevMessagesLengthRef.current || latestMessageChanged) {
      const previousLatestIndex = messages.findIndex((message) => message.id === prevLatestMessageIdRef.current);
      const newCount = previousLatestIndex >= 0
        ? messages.length - previousLatestIndex - 1
        : Math.max(1, messages.length - prevMessagesLengthRef.current);
      const isOwnMessage = latestMessage?.sender_id === user?.id;

      if (prevMessagesLengthRef.current === 0 && messages.length > 0) {
        scrollToBottom(true);
      } else {
        if (isOwnMessage) {
          scrollToBottom(false);
        } else if (isUserScrolledUp.current) {
          setUnreadInChat((prev) => prev + newCount);
        } else {
          scrollToBottom(false);
        }
      }
    }
    prevMessagesLengthRef.current = messages.length;
    prevLatestMessageIdRef.current = latestMessage?.id;
  }, [messages, selectedUser?.id]);

  const sendHeartbeat = async () => {
    try {
      await usersAPI.heartbeat();
    } catch (error) {}
  };

  const loadUsers = async () => {
    try {
      const response = await internalChatAPI.getUsers();
      const activeUsers = response.data.filter((colleague) => colleague.is_active !== false);
      setUsersList(activeUsers);
      setSelectedUser((previous) =>
        previous && activeUsers.some((colleague) => colleague.id === previous.id) ? previous : null,
      );
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnlineUsers = async () => {
    try {
      const response = await usersAPI.getOnline();
      setOnlineUsers(response.data.map((u) => u.user_id));
    } catch (error) {}
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await internalChatAPI.getUnreadCounts();
      setUnreadCounts(response.data);
    } catch (error) {}
  };

  const loadMessages = async (userId) => {
    try {
      const response = await internalChatAPI.getMessages(userId);
      if (activeRecipientIdRef.current !== userId) return;
      setMessages(response.data);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    }
  };

  const handleUserSelect = (u) => {
    setSelectedUser(u);
    loadMessages(u.id);
    setUnreadCounts((prev) => {
      const newCounts = { ...prev };
      delete newCounts[u.id];
      return newCounts;
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || selectedUser.is_active === false) return;
    setSending(true);
    try {
      const payload = {
        message: newMessage,
        quoted_message_id: replyingTo ? replyingTo.id : null,
      };

      await internalChatAPI.sendMessage(
        selectedUser.id,
        payload.message,
        payload.quoted_message_id,
      );
      
      setNewMessage("");
      
      if (selectedUser?.id) {
        sessionStorage.removeItem(`draft_internal_${selectedUser.id}`);
      }
      
      setReplyingTo(null);
      loadMessages(selectedUser.id);
      
      scrollToBottom(false, true); 
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao enviar mensagem.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
      setTimeout(() => messageInputRef.current?.focus(), 50);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedUser || selectedUser.is_active === false) return;
    setUploading(true);
    try {
      await internalChatAPI.uploadAttachment(
        selectedUser.id,
        file,
        replyingTo ? replyingTo.id : null,
      );
      setReplyingTo(null);
      loadMessages(selectedUser.id);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao enviar arquivo.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileCapture = (file) => {
    if (!file) return;
    const fakeEvent = { target: { files: [file] } };
    handleFileUpload(fakeEvent);
  };

  const handleInputPaste = (e) => {
    if (
      e.clipboardData &&
      e.clipboardData.files &&
      e.clipboardData.files.length > 0
    ) {
      e.preventDefault();
      handleFileCapture(e.clipboardData.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedUser) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!selectedUser) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileCapture(e.dataTransfer.files[0]);
    }
  };

  const handleReplyClick = (msg) => {
    setReplyingTo(msg);
    setTimeout(() => messageInputRef.current?.focus(), 50);
  };

  const handleSaveEdit = async (msgId) => {
    setIsSavingEdit(true);
    try {
      await internalChatAPI.editMessage(msgId, { new_message: editValue });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, message: editValue, is_edited: true } : m,
        ),
      );
      setEditingMessageId(null);
    } catch (error) {
      toast({ title: "Erro ao editar", variant: "destructive" });
    } finally {
      setIsSavingEdit(false);
    }
  };

  useEffect(() => {
    if (!editingMessageId) return;
    if (editInputRef.current) {
      const input = editInputRef.current;
      input.focus();
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  }, [editingMessageId]);

  const handleDeleteMsg = async (msgId) => {
    if (deletingId) return;
    const { dismiss } = toast({
      title: "Apagar mensagem?",
      description: "A mensagem será apagada do chat.",
      duration: 10000,
      action: (
        <div className="flex gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={async (e) => {
              const btn = e.currentTarget;
              btn.disabled = true;
              btn.innerHTML = "Apagando...";
              setDeletingId(msgId);
              try {
                await internalChatAPI.deleteMessage(msgId);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === msgId
                      ? {
                          ...m,
                          message: "🚫 Mensagem apagada",
                          is_deleted: true,
                        }
                      : m,
                  ),
                );
                dismiss();
              } catch (err) {
                btn.disabled = false;
                btn.innerHTML = "Confirmar";
                toast({ title: "Erro ao apagar", variant: "destructive" });
              } finally {
                setDeletingId(null);
              }
            }}
          >
            {" "}
            Confirmar{" "}
          </Button>
          <Button variant="outline" size="sm" onClick={() => dismiss()}>
            {" "}
            Cancelar{" "}
          </Button>
        </div>
      ),
    });
  };

  const startTimer = () => {
    clearInterval(timerRef.current);
    recordingStartedAtRef.current = performance.now();
    timerRef.current = setInterval(() => {
      setRecordingTime((recordingElapsedRef.current + performance.now() - recordingStartedAtRef.current) / 1000);
    }, 200);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recordingStartedAtRef.current !== null) {
      recordingElapsedRef.current += performance.now() - recordingStartedAtRef.current;
      recordingStartedAtRef.current = null;
      setRecordingTime(recordingElapsedRef.current / 1000);
    }
  };

  const clearAudioPreview = () => {
    audioPreviewRef.current?.pause();
    if (audioPreviewUrlRef.current) {
      URL.revokeObjectURL(audioPreviewUrlRef.current);
      audioPreviewUrlRef.current = null;
    }
    setRecordedAudio(null);
  };

  const handleStartRecording = async () => {
    if (
      !canSendAudio || uploading || sending || recordedAudio || mediaRecorderRef.current ||
      recordingRequestRef.current || audioSendInFlightRef.current
    ) return;

    const request = {};
    recordingRequestRef.current = request;
    setIsStartingRecording(true);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (recordingRequestRef.current !== request) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      const preparePreview = () => {
        const contentType = recorder.mimeType || "audio/webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: contentType });
        if (!audioBlob.size) return null;
        const extension = contentType.includes("mp4") ? "m4a" : contentType.includes("ogg") ? "ogg" : "webm";
        const file = new File([audioBlob], `audio_${Date.now()}.${extension}`, { type: contentType });
        clearAudioPreview();
        const audio = { file, url: URL.createObjectURL(audioBlob), recipientId };
        audioPreviewUrlRef.current = audio.url;
        setRecordedAudio(audio);
        return audio;
      };

      recorder.ondataavailable = (e) => {
        if (mediaRecorderRef.current !== recorder) return;
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
        if (recorder.state === "paused" && !finishRecordingRef.current) {
          preparePreview();
          setIsPreparingAudio(false);
        }
      };

      recorder.onstop = () => {
        if (mediaRecorderRef.current !== recorder) return;
        mediaRecorderRef.current = null;
        recorder.stream.getTracks().forEach((track) => track.stop());
        stopTimer();
        setIsRecording(false);
        setIsPaused(false);
        setIsPreparingAudio(false);
        const audio = preparePreview();
        audioChunksRef.current = [];
        const finish = finishRecordingRef.current;
        finishRecordingRef.current = null;
        finish?.(audio);

        if (!audio) {
          toast({ title: "Nenhum áudio gravado", description: "Grave novamente antes de enviar.", variant: "destructive" });
        }
      };

      recorder.start();
      setIsRecording(true);
      setIsPaused(false);
      recordingElapsedRef.current = 0;
      setRecordingTime(0);
      startTimer();
    } catch (err) {
      stream?.getTracks().forEach((track) => track.stop());
      if (recordingRequestRef.current !== request) return;
      mediaRecorderRef.current = null;
      toast({
        title: "Microfone bloqueado",
        description:
          "Verifique se o navegador tem permissão para acessar o microfone.",
        variant: "destructive",
      });
    } finally {
      if (recordingRequestRef.current === request) {
        recordingRequestRef.current = null;
        setIsStartingRecording(false);
      }
    }
  };

  const handleSendRecordedAudio = async () => {
    if ((!recordedAudio && !isRecording) || audioSendInFlightRef.current || uploading || sending || !canSendAudio) return;
    if (recordedAudio && recordedAudio.recipientId !== recipientId) return;

    audioSendInFlightRef.current = true;
    setUploading(true);
    audioPreviewRef.current?.pause();

    try {
      let audio = recordedAudio;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        const finished = new Promise((resolve) => { finishRecordingRef.current = resolve; });
        setIsPreparingAudio(true);
        stopTimer();
        recorder.stop();
        recorder.stream.getTracks().forEach((track) => track.stop());
        audio = await finished;
      }
      if (!audio || activeRecipientIdRef.current !== audio.recipientId) return;
      const quotedId = replyingTo ? replyingTo.id : null;
      await internalChatAPI.uploadAttachment(audio.recipientId, audio.file, quotedId);

      if (activeRecipientIdRef.current === audio.recipientId) {
        clearAudioPreview();
        setRecordingTime(0);
        setReplyingTo(null);
        loadMessages(audio.recipientId);
        toast({ title: "Áudio enviado" });
      }
    } catch (error) {
      if (activeRecipientIdRef.current === recipientId) {
        toast({ title: "Erro ao enviar áudio", description: "O áudio foi mantido para você tentar novamente.", variant: "destructive" });
      }
    } finally {
      finishRecordingRef.current = null;
      setIsPreparingAudio(false);
      audioSendInFlightRef.current = false;
      setUploading(false);
    }
  };

  const handlePauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();
      setIsPreparingAudio(true);
      mediaRecorderRef.current.requestData();
    }
  };

  const handleResumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      clearAudioPreview();
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    }
  };

  const handleCancelRecording = () => {
    recordingRequestRef.current = null;
    audioChunksRef.current = [];
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      if (recorder.state !== "inactive") recorder.stop();
      recorder.stream.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
    }
    stopTimer();
    clearAudioPreview();
    setIsStartingRecording(false);
    setIsPreparingAudio(false);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  const memoizedMessages = React.useMemo(() => {
    return messages.map((msg, index) => {
      const isOwnMessage = msg.sender_id === user?.id;
      const isEditing = editingMessageId === msg.id;

      return (
        <div
          id={`msg-row-${msg.id}`}
          key={msg.id || index}
          className={`flex group ${isOwnMessage ? "justify-end" : "justify-start"} w-full transition-colors duration-500 p-1 rounded-lg`}
        >
          <div
            className={`relative flex items-center gap-2 max-w-[90%] lg:max-w-[85%] ${isOwnMessage ? "flex-row-reverse" : "flex-row"}`}
          >
            <div
              className={`px-3 py-2 rounded-2xl shadow-sm transition-all flex flex-col min-w-[160px] max-w-full overflow-hidden ${
                isOwnMessage
                  ? "bg-[#d9fdd3] text-gray-900 rounded-tr-none"
                  : "bg-white text-gray-900 rounded-tl-none border border-gray-100"
              } ${msg.is_deleted ? "opacity-70 italic" : ""}`}
            >
              {msg.quoted_message_id &&
                (() => {
                  const quotedMsg = messages.find(
                    (m) => m.id === msg.quoted_message_id,
                  );
                  if (!quotedMsg) return null;

                  const isQuotedOwn = quotedMsg.sender_id === user?.id;
                  const senderColor = isQuotedOwn
                    ? "text-emerald-600"
                    : "text-purple-600";

                  return (
                    <div
                      onClick={() => {
                        const el = document.getElementById(
                          `msg-row-${quotedMsg.id}`,
                        );
                        if (el) {
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                          el.classList.add("bg-blue-500/20");
                          setTimeout(() => {
                            el.classList.remove("bg-blue-500/20");
                          }, 1500);
                        }
                      }}
                      className={`mb-2 p-2 rounded-lg cursor-pointer border-l-[4px] text-sm opacity-95 hover:opacity-100 transition-all ${
                        isOwnMessage
                          ? "bg-[#cbf4c9] border-emerald-500 hover:bg-[#bbf0b9]"
                          : "bg-gray-100 border-purple-500 hover:bg-gray-200"
                      }`}
                    >
                      <span
                        className={`font-bold block mb-0.5 text-xs tracking-wider ${senderColor}`}
                      >
                        {isQuotedOwn ? "Voc\u00ea" : quotedMsg.sender_name}
                      </span>
                      <span className="block line-clamp-2 text-gray-700 leading-snug break-words whitespace-pre-wrap">
                        {quotedMsg.message ||
                          (quotedMsg.attachment
                            ? "Arquivo/M\u00eddia"
                            : "Mensagem")}
                      </span>
                    </div>
                  );
                })()}

              <div className="flex justify-between items-center gap-3 mb-1.5">
                <span
                  className={`text-xs font-bold tracking-wider truncate ${isOwnMessage ? "text-emerald-600" : "text-purple-600"}`}
                >
                  {isOwnMessage ? "Você" : msg.sender_name.split(" ")[0]}
                </span>
                <div className="flex items-center gap-1 opacity-70 shrink-0">
                  {msg.is_edited && !msg.is_deleted && (
                    <span className="text-xs italic flex items-center gap-0.5">
                      <Pencil className="w-2 h-2" /> editada
                    </span>
                  )}
                  <span className="text-xs font-medium tracking-wide">
                    {formatTime(msg.timestamp)}
                  </span>
                  {isOwnMessage && !msg.is_deleted && (
                    <div className="ml-0.5">{renderStatus(msg)}</div>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="flex flex-col gap-2 w-full min-w-[200px] sm:min-w-[350px]">
                  <textarea
                    ref={editInputRef}
                    disabled={isSavingEdit}
                    className="w-full bg-white/50 border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none min-h-[80px]"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !isSavingEdit) {
                        e.preventDefault();
                        handleSaveEdit(msg.id);
                      }
                    }}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingMessageId(null)}
                      className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleSaveEdit(msg.id)}
                      className="p-1.5 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      {isSavingEdit ? (
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  {msg.message && !msg.is_deleted && (
                    <p className="whitespace-pre-wrap break-words text-sm sm:text-base text-gray-800">
                      {msg.message}
                    </p>
                  )}
                  {msg.is_deleted && (
                    <p className="text-sm sm:text-base text-gray-500">
                      🚫 Esta mensagem foi apagada
                    </p>
                  )}
                </div>
              )}

              {!msg.is_deleted &&
                msg.attachment &&
                (() => {
                  const att = msg.attachment;
                  const url = att.url || att.file_url || att.path || "";
                  const contentType = att.content_type || att.mime_type || "";
                  const filename = att.filename || att.name || "arquivo";

                  if (!url)
                    return (
                      <p className="text-sm italic text-gray-500">
                        📎 Anexo: {filename}
                      </p>
                    );

                  const fullUrl = url.startsWith("http")
                    ? url
                    : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;

                  if (
                    contentType.includes("image") ||
                    filename.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i)
                  ) {
                    const isSticker = filename.endsWith(".webp");
                    return (
                      <div
                        onClick={() => {
                          setZoomedImage(fullUrl);
                          setZoomScale(1);
                        }}
                        className="mt-1 block cursor-zoom-in group/img relative"
                      >
                        <img
                          src={fullUrl}
                          alt="Anexo"
                          onLoad={() => scrollToBottom(true, true)}
                          className={`rounded-lg object-contain transition-all bg-black/5 group-hover/img:brightness-90 ${isSticker ? "max-h-32 max-w-[128px]" : "max-w-full max-h-[300px]"}`}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML = `<p class="text-sm text-gray-600">🖼️ Imagem: ${filename}</p>`;
                          }}
                        />
                      </div>
                    );
                  }
                  if (
                    contentType.includes("audio") ||
                    filename.match(/\.(webm|ogg|mp3|wav|m4a|aac|opus)$/i)
                  ) {
                    return (
                      <audio
                        controls
                        src={fullUrl}
                        className="w-full sm:min-w-[250px] h-10 mt-1.5 outline-none"
                      />
                    );
                  }
                  if (
                    contentType.includes("video") ||
                    filename.match(/\.(mp4|mov|avi|mkv)$/i)
                  ) {
                    return (
                      <video
                        controls
                        src={fullUrl}
                        className="max-w-full rounded-lg max-h-[300px] mt-1 bg-black/5"
                      />
                    );
                  }
                  return (
                    <a
                      href={fullUrl}
                      onClick={(e) => handleDownload(e, fullUrl, filename)}
                      className="flex items-center gap-2 bg-black/5 mt-1.5 p-2 rounded-lg hover:bg-black/10 text-sm text-gray-700 transition-colors border border-black/5 cursor-pointer"
                    >
                      <div className="bg-white p-1.5 rounded-md shadow-sm shrink-0">
                        <Download className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="truncate font-medium max-w-[200px]">
                          {filename}
                        </span>
                        <span className="text-[10px] text-gray-500 uppercase">
                          Clique para baixar
                        </span>
                      </div>
                    </a>
                  );
                })()}
            </div>

            {!msg.is_deleted && !isEditing && (
              <div className="flex flex-col items-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={() => handleReplyClick(msg)}
                  className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                  title="Responder"
                >
                  <Reply className="w-3 h-3" />
                </button>
                {isOwnMessage && (
                  <>
                    <div className="flex flex-row gap-2">
                      <button
                        onClick={() => {
                          setEditingMessageId(msg.id);
                          setEditValue(msg.message);
                        }}
                        className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                        title="Editar"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteMsg(msg.id)}
                        disabled={deletingId === msg.id}
                        className="p-1.5 bg-white shadow-sm border border-gray-200 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                        title="Apagar"
                      >
                        {deletingId === msg.id ? (
                          <div className="animate-spin w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });
  }, [
    messages,
    user?.id,
    editingMessageId,
    editValue,
    isSavingEdit,
    deletingId,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div
      className="h-full w-full flex bg-gray-50 overflow-hidden rounded-lg shadow-sm"
      data-testid="internal-chat-page"
    >
      <div
        className={`border-r flex-col bg-white shrink-0 z-10 transition-all duration-300 ${
          selectedUser ? "hidden md:flex w-80" : "flex w-full md:w-80"
        }`}
      >
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 shadow-sm relative">
          <div className="flex items-center justify-center gap-2">
            <Users className="w-5 h-5" />
            <h2 className="font-semibold tracking-wide">Equipe</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
          {usersList.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm mt-10">
              Nenhum colega disponível no momento.
            </div>
          ) : (
            usersList.map((u) => {
              const isSelected = selectedUser?.id === u.id;
              return (
                <div
                  key={u.id}
                  onClick={() => handleUserSelect(u)}
                  className={`p-4 border-b border-l-4 cursor-pointer transition-all duration-200 group relative ${
                    isSelected
                      ? "bg-orange-50 border-l-blue-600"
                      : "bg-white border-l-transparent hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            isSelected
                              ? "bg-blue-600 text-white shadow-md"
                              : "bg-blue-100 text-blue-600"
                          }`}
                        >
                          {u.name?.charAt(0)}
                        </div>
                        <Circle
                          className={`w-3.5 h-3.5 absolute -bottom-0.5 -right-0.5 border-2 border-white rounded-full ${
                            isUserOnline(u.id)
                              ? "fill-green-500 text-green-500"
                              : "fill-gray-300 text-gray-300"
                          }`}
                        />
                      </div>
                      <div>
                        <p
                          className={`font-semibold text-sm truncate max-w-[160px] md:max-w-[140px] ${
                            isSelected
                              ? "text-blue-700"
                              : "text-gray-900 group-hover:text-blue-600"
                          }`}
                        >
                          {u.name}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          {u.is_active === false
                            ? "Inativo"
                            : u.role === "admin"
                              ? "Administrador"
                              : u.role === "analyst"
                                ? "Analista"
                                : "Atendente"}
                        </p>
                      </div>
                    </div>
                    {unreadCounts[u.id] > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-sm shrink-0">
                        {unreadCounts[u.id]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div
        className={`flex-1 flex-col relative h-full min-w-0 z-0 bg-[#efeae2] ${
          !selectedUser ? "hidden md:flex" : "flex"
        }`}
      >
        {!selectedUser ? (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50/80">
            <div className="w-24 h-24 bg-blue-100/50 rounded-full flex items-center justify-center mb-6 shadow-sm ring-4 ring-white">
              <MessageCircle className="w-10 h-10 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
              Chat da Equipe
            </h2>
            <p className="text-gray-500 text-center max-w-sm px-4 font-medium">
              Selecione um colega na barra lateral para iniciar uma conversa.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white border-b px-4 md:px-6 py-3 sm:py-4 z-10 shadow-sm sticky top-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedUser(null)}
                  className="md:hidden mr-1 text-gray-600 hover:bg-gray-100 shrink-0"
                  title="Voltar"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold shadow-md">
                    {selectedUser.name?.charAt(0)}
                  </div>
                  <Circle
                    className={`w-3.5 h-3.5 absolute -bottom-0.5 -right-0.5 border-2 border-white rounded-full ${
                      isUserOnline(selectedUser.id)
                        ? "fill-green-500 text-green-500"
                        : "fill-gray-300 text-gray-300"
                    }`}
                  />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 text-base md:text-lg leading-tight truncate max-w-[200px] sm:max-w-md">
                    {selectedUser.name}
                  </h2>
                  <p className="text-xs font-medium text-gray-500">
                    {selectedUser.is_active === false ? (
                      "Inativo"
                    ) : isUserOnline(selectedUser.id) ? (
                      <span className="text-green-600">Online agora</span>
                    ) : (
                      "Offline"
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto custom-scrollbar"
              ref={chatContainerRef}
              onScroll={handleScroll}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div className="absolute inset-0 z-[9999] bg-blue-500/80 backdrop-blur-sm flex flex-col items-center justify-center m-2 border-4 border-dashed border-white rounded-xl">
                  <div className="bg-white p-4 rounded-full mb-4 animate-bounce shadow-xl">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </div>
                  <h2 className="text-3xl font-black text-white tracking-wider drop-shadow-md text-center px-4">
                    SOLTE O ARQUIVO AQUI
                  </h2>
                  <p className="text-blue-100 mt-2 font-medium">
                    Enviar para {selectedUser.name.split(" ")[0]}
                  </p>
                </div>
              )}

              <div className="max-w-full mx-auto px-2 sm:px-6 py-6 flex flex-col min-h-full gap-1.5">
                <div className="flex-1 min-h-[20px]"></div>

                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-10 bg-white/70 backdrop-blur-sm rounded-xl w-full max-w-sm mx-auto p-8 shadow-sm border border-gray-100 mb-auto mt-auto">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-semibold text-gray-700">
                      Nenhuma mensagem ainda
                    </p>
                    <p className="text-sm mt-1">Diga um "Oi" para começar!</p>
                  </div>
                ) : (
                  memoizedMessages
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {isScrolledUp && unreadInChat > 0 && (
              <button
                onClick={() => scrollToBottom(false)}
                className="absolute bottom-[90px] right-6 z-50 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 shadow-xl rounded-full text-gray-500 hover:text-blue-600 transition-all animate-in slide-in-from-bottom-5 hover:scale-110"
              >
                <ChevronDown className="w-6 h-6" />
                <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full shadow-sm border-2 border-white">
                  {unreadInChat}
                </span>
              </button>
            )}

            <div className="bg-gray-200/80 border-t p-3 relative backdrop-blur-md z-20">
              {replyingTo && (
                <div className="w-full lg:max-w-6xl xl:max-w-7xl mx-auto mb-2 flex items-center justify-between bg-gray-100 border-l-4 border-blue-500 p-2.5 rounded-lg shadow-sm animate-in fade-in slide-in-from-bottom-2">
                  <div className="flex flex-col truncate pr-4">
                    <span className="font-bold text-blue-600 text-xs tracking-wider mb-0.5 uppercase">
                      Respondendo a:{" "}
                      {replyingTo.sender_id === user?.id
                        ? "Você"
                        : replyingTo.sender_name.split(" ")[0]}
                    </span>
                    <span className="text-gray-600 text-sm truncate max-w-[300px] sm:max-w-md">
                      {replyingTo.message || "📎 Arquivo/Anexo"}
                    </span>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="w-full lg:max-w-6xl xl:max-w-7xl mx-auto relative flex items-center gap-2">
                {selectedUser.is_active === false ? (
                  <p className="w-full text-center text-sm text-gray-500 py-3">
                    Usuário inativo. O histórico está disponível para consulta.
                  </p>
                ) : isRecording || recordedAudio || isPreparingAudio ? (
                  <VoiceRecordingControls
                    isRecording={isRecording}
                    isPaused={isPaused}
                    isPreparing={isPreparingAudio}
                    isSending={uploading}
                    audioUrl={recordedAudio?.url}
                    duration={recordingTime}
                    stream={mediaRecorderRef.current?.stream}
                    audioRef={audioPreviewRef}
                    onPause={handlePauseRecording}
                    onResume={handleResumeRecording}
                    onDiscard={handleCancelRecording}
                    onSend={handleSendRecordedAudio}
                    onPlaybackError={() => toast({
                      title: "Não foi possível reproduzir a prévia",
                      description: "Você ainda pode continuar a gravação ou enviar o áudio.",
                      variant: "destructive",
                    })}
                  />
                ) : (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                    />

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || sending}
                      className="text-gray-500 hover:bg-gray-300 hover:text-gray-800 shrink-0 h-11 w-11 rounded-full transition-colors"
                    >
                      {uploading ? (
                        <div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                      ) : (
                        <Paperclip className="w-5 h-5" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleStartRecording}
                      disabled={uploading || sending || isStartingRecording}
                      title="Gravar áudio"
                      aria-label="Gravar áudio"
                      className="text-gray-500 hover:bg-gray-300 hover:text-gray-800 shrink-0 h-11 w-11 rounded-full transition-colors"
                    >
                      {isStartingRecording ? (
                        <div className="animate-spin w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </Button>

                    <Textarea
                      ref={messageInputRef}
                      placeholder="Digite uma mensagem para a equipe..."
                      value={newMessage}
                      onChange={handleMessageChange}
                      onPaste={handleInputPaste}
                      disabled={sending || uploading}
                      rows={1}
                      className="flex-1 border-none shadow-sm focus-visible:ring-0 bg-white rounded-2xl px-5 py-4 text-base resize-none overflow-y-auto min-h-[56px] leading-relaxed"
                      style={{ maxHeight: "120px" }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (!sending && !uploading) {
                            handleSendMessage();
                            scrollToBottom(false, true);
                          }
                        }
                      }}
                    />

                    {newMessage.trim() && (
                      <Button
                        onClick={handleSendMessage}
                        disabled={sending}
                        size="icon"
                        className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 w-11 shadow-sm transition-transform hover:scale-105 animate-in zoom-in-75 duration-200"
                      >
                        {sending ? (
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Send className="w-4 h-4 ml-0.5" />
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {zoomedImage && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm animate-in fade-in duration-200">
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={5}
            centerOnInit={true}
            limitToBounds={true}
            wheel={{ step: 0.005 }}
            pinch={{ step: 5 }}
          >
            {({ zoomIn, zoomOut, resetTransform, state }) => (
              <React.Fragment>
                <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 sm:p-6 z-10 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
                  <div className="flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5 backdrop-blur-md border border-white/20 text-white pointer-events-auto shadow-lg">
                    <button
                      onClick={() => zoomOut()}
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      title="Diminuir"
                    >
                      <ZoomOut className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <span className="font-mono font-medium w-12 text-center text-sm sm:text-base">
                      {Math.round((state?.scale || 1) * 100)}%
                    </span>

                    <button
                      onClick={() => zoomIn()}
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                      title="Aumentar"
                    >
                      <ZoomIn className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <div className="w-px h-5 bg-white/20 mx-1"></div>

                    <button
                      onClick={(e) =>
                        handleDownload(e, zoomedImage, "anexo_imagem.jpg")
                      }
                      className="p-1.5 hover:bg-white/20 rounded-full transition-colors text-blue-400"
                      title="Baixar Imagem"
                    >
                      <Download className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setZoomedImage(null);
                      resetTransform();
                    }}
                    className="p-2 hover:bg-white/20 rounded-full text-white transition-colors bg-black/50 backdrop-blur-md border border-white/20 pointer-events-auto shadow-lg"
                    title="Fechar (Esc)"
                  >
                    <X className="w-6 h-6 sm:w-8 sm:h-8" />
                  </button>
                </div>

                <div className="flex-1 w-full h-full flex items-center justify-center overflow-hidden">
                  <TransformComponent
                    wrapperStyle={{ width: "100%", height: "100%" }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={zoomedImage}
                      alt="Zoom"
                      className="max-w-full max-h-[100vh] object-contain shadow-2xl cursor-grab active:cursor-grabbing"
                    />
                  </TransformComponent>
                </div>
              </React.Fragment>
            )}
          </TransformWrapper>
        </div>
      )}
    </div>
  );
};

export default InternalChat;
